import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Mail, Inbox, FileUp, BarChart3, Zap, Users, Check, ArrowRight, Sparkles, Star } from 'lucide-react';
import { landingFeatures, landingTestimonials, BRAND } from '../mock/mock';
import SEO from '../components/SEO';

const iconMap = { Mail, Inbox, FileUp, BarChart3, Zap, Users };

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="bg-[#0a0a0b] text-zinc-100">
      <SEO
        title="Reachly — cold outreach for job seekers | Multi-step email campaigns"
        description="Land interviews faster. Build multi-step email campaigns to recruiters and hiring managers, automate follow-ups, and track every reply — sent from your own Outlook inbox."
        canonical={typeof window !== 'undefined' ? window.location.origin + '/' : ''}
        jsonLd={{
          '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'Reachly',
          applicationCategory: 'BusinessApplication', operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '218' }
        }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden hero-glow">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-20 pb-24 relative z-10">
          <div className="flex justify-center mb-6 animate-fade-up">
            <Badge className="bg-[#1a1815] text-[#f5d67a] border border-[#3a2f18] font-normal rounded-full px-3 py-1">
              <Sparkles size={12} className="mr-1.5" /> Land interviews, not busywork
            </Badge>
          </div>
          <h1 className="font-display text-center text-5xl md:text-7xl font-medium leading-[1.05] animate-fade-up">
            The job seeker's<br/>
            <span className="gold-text">cold outreach machine.</span>
          </h1>
          <p className="mt-7 max-w-2xl mx-auto text-center text-lg text-zinc-400 leading-relaxed animate-fade-up">
            {BRAND.description}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 animate-fade-up">
            <Button size="lg" className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium h-12 px-7" onClick={() => navigate('/signup')}>
              Start free — no card <ArrowRight size={16} className="ml-1" />
            </Button>
            <Button size="lg" variant="outline" className="border-zinc-800 bg-transparent text-zinc-200 hover:bg-zinc-900 hover:text-[#f5d67a] h-12 px-7" onClick={() => navigate('/app')}>
              Watch it work
            </Button>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-6 text-xs text-zinc-500">
            <div className="flex items-center gap-1.5"><Check size={14} className="text-[#d4a94a]" /> Send from your Outlook</div>
            <div className="flex items-center gap-1.5"><Check size={14} className="text-[#d4a94a]" /> Auto follow-ups</div>
            <div className="flex items-center gap-1.5"><Check size={14} className="text-[#d4a94a]" /> Free forever plan</div>
          </div>

          {/* Product preview */}
          <div className="mt-16 relative animate-fade-up">
            <div className="rounded-xl border border-zinc-800 bg-gradient-to-b from-[#141311] to-[#0e0d0c] p-2 shadow-[0_40px_80px_-20px_rgba(212,169,74,0.15)]">
              <div className="rounded-lg overflow-hidden bg-[#0a0a0b] border border-zinc-900">
                <div className="grid grid-cols-12 min-h-[380px]">
                  <div className="col-span-3 border-r border-zinc-900 p-4 space-y-2">
                    <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Campaigns</div>
                    {['Series-B Startups','FAANG Recruiters','AI Startups — SF'].map((n,i) => (
                      <div key={n} className={`px-3 py-2 rounded-md text-sm border ${i===0?'bg-[#1a1815] border-[#3a2f18] text-[#f5d67a]':'border-transparent text-zinc-400'}`}>{n}</div>
                    ))}
                  </div>
                  <div className="col-span-9 p-6">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <div className="font-display text-xl">Series-B Startups Outreach</div>
                        <div className="text-xs text-zinc-500 mt-1">3 steps · 42 contacts · 128 sent</div>
                      </div>
                      <Badge className="bg-emerald-950 text-emerald-300 border border-emerald-900">Active</Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        {label:'Sent',value:'128'},
                        {label:'Opens',value:'84 (65.6%)'},
                        {label:'Replies',value:'17 (13.3%)'},
                      ].map(s => (
                        <div key={s.label} className="rounded-lg border border-zinc-800 p-4">
                          <div className="text-xs text-zinc-500">{s.label}</div>
                          <div className="mt-1 font-display text-2xl gold-text">{s.value}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 space-y-2">
                      {[
                        {step:'Step 1 · Day 0',subj:'Interested in Senior Recruiter opportunities at Stripe',state:'Sent • 128'},
                        {step:'Step 2 · Day 3',subj:'Re: Senior Recruiter at Stripe',state:'Sent • 84'},
                        {step:'Step 3 · Day 7',subj:'Last note — Stripe',state:'Scheduled • 32'},
                      ].map((r)=> (
                        <div key={r.step} className="flex items-center justify-between rounded-md border border-zinc-900 px-4 py-3">
                          <div>
                            <div className="text-xs text-zinc-500">{r.step}</div>
                            <div className="text-sm text-zinc-200">{r.subj}</div>
                          </div>
                          <div className="text-xs text-zinc-500">{r.state}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-sm uppercase tracking-widest text-[#d4a94a]">The workflow</div>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">Everything you need to run a real outreach campaign.</h2>
            <p className="mt-4 text-zinc-400">Built specifically for job seekers, not sales teams. No B2B database bloat. Just the sequencing engine you actually need.</p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {landingFeatures.map((f) => {
              const Icon = iconMap[f.icon] || Mail;
              return (
                <Card key={f.title} className="bg-[#111110] border-zinc-900 p-6 hover:border-[#3a2f18] transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-[#1a1815] border border-[#3a2f18] flex items-center justify-center text-[#f5d67a] mb-4">
                    <Icon size={18} />
                  </div>
                  <div className="font-display text-xl text-zinc-100">{f.title}</div>
                  <div className="mt-2 text-sm text-zinc-400 leading-relaxed">{f.desc}</div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-sm uppercase tracking-widest text-[#d4a94a]">How it works</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl max-w-2xl">From resume to reply in four steps.</h2>
          <div className="mt-14 grid md:grid-cols-4 gap-4">
            {[
              {n:'01',t:'Connect Outlook',d:'Sign in with Microsoft. We send from your inbox, replies come to your inbox.'},
              {n:'02',t:'Import contacts',d:'Upload a CV or CSV. AI extracts names, emails, phones, companies, titles.'},
              {n:'03',t:'Design a sequence',d:'Write your intro + follow-ups with {{first_name}}, {{company}} personalization.'},
              {n:'04',t:'Launch — we handle the rest',d:'Auto-scheduled follow-ups. Replies pause the sequence for that contact instantly.'},
            ].map(s => (
              <div key={s.n} className="rounded-lg border border-zinc-900 p-6 bg-[#0f0e0d]">
                <div className="font-display text-4xl gold-text">{s.n}</div>
                <div className="mt-3 font-display text-xl">{s.t}</div>
                <div className="mt-2 text-sm text-zinc-400">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-sm uppercase tracking-widest text-[#d4a94a]">Job offers, in the wild</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl max-w-2xl">Real outreach. Real offers.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {landingTestimonials.map(t => (
              <Card key={t.name} className="bg-[#111110] border-zinc-900 p-6">
                <div className="flex gap-1 text-[#d4a94a]">{['s1','s2','s3','s4','s5'].map((k)=><Star key={k} size={14} fill="#d4a94a" strokeWidth={0}/>)}</div>
                <p className="mt-4 text-zinc-200 leading-relaxed">“{t.quote}”</p>
                <div className="mt-6">
                  <div className="text-sm text-zinc-100">{t.name}</div>
                  <div className="text-xs text-zinc-500">{t.role}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-sm uppercase tracking-widest text-[#d4a94a]">Pricing</div>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">One plan to land the job.</h2>
            <p className="mt-4 text-zinc-400">Start free. Upgrade only when your first offer comes in.</p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            <Card className="bg-[#111110] border-zinc-900 p-8">
              <div className="font-display text-xl">Free</div>
              <div className="mt-4 flex items-baseline gap-1"><span className="font-display text-5xl">$0</span><span className="text-zinc-500">/mo</span></div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-300">
                {['1 active campaign','25 emails / day','Outlook OAuth','CSV import','Basic analytics'].map(f=><li key={f} className="flex gap-2"><Check size={16} className="text-[#d4a94a]"/>{f}</li>)}
              </ul>
              <Button className="mt-8 w-full bg-zinc-900 hover:bg-zinc-800 text-zinc-100" onClick={()=>navigate('/signup')}>Start free</Button>
            </Card>
            <Card className="bg-gradient-to-b from-[#1a1613] to-[#141110] border-[#3a2f18] p-8 relative">
              <Badge className="absolute -top-2 right-6 bg-[#d4a94a] text-black hover:bg-[#d4a94a]">Popular</Badge>
              <div className="font-display text-xl gold-text">Pro</div>
              <div className="mt-4 flex items-baseline gap-1"><span className="font-display text-5xl gold-text">$19</span><span className="text-zinc-400">/mo</span></div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-200">
                {['Unlimited campaigns','200 emails / day','AI CV parsing','Advanced analytics','Priority support','Custom sending windows'].map(f=><li key={f} className="flex gap-2"><Check size={16} className="text-[#d4a94a]"/>{f}</li>)}
              </ul>
              <Button className="mt-8 w-full bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" onClick={()=>navigate('/checkout')}>Start 7-day trial</Button>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 border-t border-zinc-900">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-sm uppercase tracking-widest text-[#d4a94a]">FAQ</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">Common questions.</h2>
          <Accordion type="single" collapsible className="mt-10">
            {[
              { q:'Does Reachly send emails from my own inbox?', a:'Yes. Reachly connects to Outlook via Microsoft OAuth. Every email is sent directly from your address; replies land in your normal inbox.' },
              { q:'Will my Outlook account get flagged?', a:'We enforce daily sending limits, human-like pacing, and a 60-second minimum between messages so your reputation stays healthy.' },
              { q:'What happens when someone replies?', a:'Reachly detects the reply and immediately pauses the sequence for that contact. No awkward follow-ups after a yes.' },
              { q:'Can I upload a CV or resume?', a:'Yes. Drop a PDF or DOCX. Our AI extracts contacts, then you review and approve before anyone lands in a campaign.' },
              { q:'Is there a free plan?', a:'Yes. Free forever with 1 active campaign and 25 emails/day. Upgrade only when you need more volume.' },
            ].map((f,i)=>(
              <AccordionItem key={f.q} value={`i${i}`} className="border-zinc-900">
                <AccordionTrigger className="text-left text-zinc-100 hover:text-[#f5d67a]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-zinc-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-6xl">Your next role<br/><span className="gold-text">deserves a real campaign.</span></h2>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Button size="lg" className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black h-12 px-8" onClick={()=>navigate('/signup')}>Start free</Button>
            <Link to="/compare" className="text-sm text-zinc-400 hover:text-[#f5d67a] px-4">See how Reachly compares →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
